/**
 * Datetime utility functions for timezone-aware datetime handling
 * Converts between UTC (database) and user timezone (display/input)
 */

import { format, parseISO, formatDistanceToNow } from 'date-fns'
import { getUserTimezone } from '@/utils/timezoneUtils'

// Re-export getUserTimezone for convenience
export { getUserTimezone }

/**
 * Convert UTC ISO string to Date object in user timezone
 * The Date object will represent the same moment in time, but when displayed
 * it will show in the user's timezone
 * 
 * @param isoString - ISO 8601 string (UTC)
 * @param userTimezone - User's timezone (defaults to current user)
 * @returns Date object
 */
export function convertToUserTimezone(isoString: string | null | undefined, userTimezone?: string): Date | null {
  if (!isoString) return null
  
  try {
    // Parse ISO string (assumes UTC if no timezone specified)
    // The Date object internally stores UTC, but when displayed/formatted
    // it will be shown in the user's timezone
    const date = parseISO(isoString)
    if (!date || isNaN(date.getTime())) return null
    return date
  } catch (error) {
    console.error('Failed to convert datetime to user timezone:', error)
    return null
  }
}

/**
 * Convert Date object in user timezone to ISO string for API
 * 
 * Note: The backend TimezoneService will handle the conversion from user timezone to UTC.
 * This function formats the datetime as the user entered it (in their timezone).
 * The format should be ISO 8601 without timezone (YYYY-MM-DDTHH:mm:ss)
 * which the backend will interpret as being in the user's timezone.
 * 
 * @param date - Date object (in user timezone)
 * @param userTimezone - User's timezone (defaults to current user)
 * @returns ISO 8601 string (without timezone, for backend to interpret)
 */
export function convertFromUserTimezone(date: Date | null | undefined, userTimezone?: string): string | null {
  if (!date || isNaN(date.getTime())) return null
  
  const tz = userTimezone || getUserTimezone()
  
  try {
    // Format the date as it appears in the user's timezone
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    })
    
    const parts = formatter.formatToParts(date)
    const year = parts.find(p => p.type === 'year')?.value
    const month = parts.find(p => p.type === 'month')?.value
    const day = parts.find(p => p.type === 'day')?.value
    const hour = parts.find(p => p.type === 'hour')?.value
    const minute = parts.find(p => p.type === 'minute')?.value
    const second = parts.find(p => p.type === 'second')?.value
    
    // Return ISO format without timezone - backend will interpret as user timezone
    return `${year}-${month}-${day}T${hour}:${minute}:${second}`
  } catch (error) {
    console.error('Failed to convert datetime from user timezone:', error)
    // Fallback: use ISO string (will be interpreted as UTC by backend)
    return date.toISOString()
  }
}

/**
 * Format datetime for API (convert from user timezone to UTC ISO string)
 * 
 * @param date - Date object (in user timezone)
 * @param userTimezone - User's timezone (defaults to current user)
 * @returns ISO 8601 string (UTC)
 */
export function formatDatetimeForAPI(date: Date | null | undefined, userTimezone?: string): string | null {
  return convertFromUserTimezone(date, userTimezone)
}

/**
 * Parse datetime from API (convert from UTC ISO string to user timezone Date)
 * 
 * @param isoString - ISO 8601 string (UTC)
 * @param userTimezone - User's timezone (defaults to current user)
 * @returns Date object (in user timezone)
 */
export function parseDatetimeFromAPI(isoString: string | null | undefined, userTimezone?: string): Date | null {
  return convertToUserTimezone(isoString, userTimezone)
}

/**
 * Format datetime for display in user timezone
 * 
 * @param date - Date object or ISO string
 * @param formatString - Format string (date-fns format, default: 'yyyy-MM-dd HH:mm:ss')
 * @param userTimezone - User's timezone (defaults to current user)
 * @returns Formatted string
 */
export function formatDatetimeForDisplay(
  date: Date | string | null | undefined,
  formatString: string = 'yyyy-MM-dd HH:mm:ss',
  userTimezone?: string
): string {
  if (!date) return ''
  
  try {
    const dateObj = typeof date === 'string' ? parseISO(date) : date
    if (!dateObj || isNaN(dateObj.getTime())) return ''
    
    // Use date-fns format function which properly handles format strings
    // Note: date-fns format works with the Date object's timezone
    // The Date object already represents the correct moment in time
    return format(dateObj, formatString)
  } catch (error) {
    console.error('Failed to format datetime for display:', error)
    return ''
  }
}

/**
 * Format datetime for input fields (datetime-local format)
 * Returns format: YYYY-MM-DDTHH:mm
 * 
 * @param date - Date object or ISO string
 * @param userTimezone - User's timezone (defaults to current user)
 * @returns Formatted string for datetime-local input
 */
export function formatDatetimeForInput(
  date: Date | string | null | undefined,
  userTimezone?: string
): string {
  if (!date) return ''
  
  return formatDatetimeForDisplay(date, "yyyy-MM-dd'T'HH:mm", userTimezone)
}

/**
 * Parse datetime from input field (datetime-local format)
 * Converts from user timezone to UTC for API
 * 
 * @param inputValue - String from datetime-local input (YYYY-MM-DDTHH:mm)
 * @param userTimezone - User's timezone (defaults to current user)
 * @returns ISO 8601 string (UTC) or null
 */
export function parseDatetimeFromInput(
  inputValue: string | null | undefined,
  userTimezone?: string
): string | null {
  if (!inputValue) return null
  
  const tz = userTimezone || getUserTimezone()
  
  try {
    // Parse as if it's in user timezone
    const date = new Date(inputValue)
    
    // Convert to UTC ISO string
    return convertFromUserTimezone(date, tz)
  } catch (error) {
    console.error('Failed to parse datetime from input:', error)
    return null
  }
}

/**
 * Format datetime as relative time (e.g., "2 hours ago", "3 days ago", "just now")
 * 
 * @param dateString - ISO 8601 string or null
 * @returns Relative time string or empty string if invalid
 */
export function formatRelativeTime(dateString: string | null | undefined): string {
  if (!dateString) return ''
  
  try {
    const date = parseISO(dateString)
    if (!date || isNaN(date.getTime())) return ''
    
    // Use date-fns formatDistanceToNow for relative time
    // Add "ago" suffix for past dates
    const distance = formatDistanceToNow(date, { addSuffix: true })
    
    // Handle "just now" case for very recent times (less than 1 minute)
    const now = new Date()
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000)
    
    if (diffInSeconds < 60) {
      return 'just now'
    }
    
    return distance
  } catch (error) {
    console.error('Failed to format relative time:', error)
    return ''
  }
}

/** YYYY-MM-DD for the calendar date of `date` in `timezone` (IANA). */
function getYmdInTimezone(date: Date, timezone: string): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).format(date)
}

/** UTC day index from a YYYY-MM-DD string (calendar math, DST-safe). */
function ymdToUtcDayIndex(ymd: string): number {
  const [y, m, d] = ymd.split('-').map(Number)
  return Math.floor(Date.UTC(y, m - 1, d) / 86400000)
}

/**
 * Calendar-day distance from `event` to `now`, measured in `timezone`:
 * how many local calendar days `now` is after `event` (0 = same local day).
 */
function calendarDaysFromEventToNow(event: Date, now: Date, timezone: string): number {
  return ymdToUtcDayIndex(getYmdInTimezone(now, timezone)) - ymdToUtcDayIndex(getYmdInTimezone(event, timezone))
}

/**
 * Absolute display: "Mar 15, 5:00 PM" (current year in TZ) or "Mar 15, 2024, 5:00 PM" (other years).
 */
function formatAbsoluteHumanFriendlyInTimezone(date: Date, timezone: string, now: Date): string {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  }).formatToParts(date)

  const get = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find(p => p.type === type)?.value ?? ''

  const month = get('month')
  const day = get('day')
  const year = get('year')
  const hour = get('hour')
  const minute = get('minute')
  const dayPeriod = get('dayPeriod')

  const nowYear = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    year: 'numeric'
  })
    .formatToParts(now)
    .find(p => p.type === 'year')?.value

  const timePart = `${hour}:${minute} ${dayPeriod}`

  if (year === nowYear) {
    return `${month} ${day}, ${timePart}`
  }
  return `${month} ${day}, ${year}, ${timePart}`
}

/**
 * Human-friendly UTC ISO timestamp for PIM-style display: relative labels for recent
 * calendar days in the user's timezone, then short month + time (year only if not current year).
 *
 * @param isoString - ISO 8601 string (UTC) or null/undefined
 * @param timezone - Optional IANA timezone (defaults to getUserTimezone())
 * @returns e.g. "Today", "3 days ago", "Mar 15, 5:00 PM", or "—" for invalid input
 */
export function formatHumanFriendlyUtcTimestamp(
  isoString: string | null | undefined,
  timezone?: string
): string {
  if (isoString == null || isoString.trim() === '') return '—'
  try {
    const date = parseISO(isoString)
    if (!date || isNaN(date.getTime())) return '—'
    const tz = timezone ?? getUserTimezone()
    const now = new Date()
    const dayDiff = calendarDaysFromEventToNow(date, now, tz)

    if (dayDiff < 0) {
      return formatAbsoluteHumanFriendlyInTimezone(date, tz, now)
    }
    if (dayDiff === 0) return 'Today'
    if (dayDiff === 1) return 'Yesterday'
    if (dayDiff >= 2 && dayDiff <= 6) return `${dayDiff} days ago`
    if (dayDiff === 7) return '1 week ago'
    return formatAbsoluteHumanFriendlyInTimezone(date, tz, now)
  } catch {
    return '—'
  }
}

/**
 * Format UTC ISO timestamp for table display in user timezone (or Eastern fallback).
 * Single place for "UTC ISO → table display in chosen timezone" (e.g. Products table Created/Last Modified).
 *
 * @param isoString - ISO 8601 string (UTC) or null/undefined
 * @param timezone - Optional timezone (defaults to getUserTimezone() = user saved or America/New_York)
 * @returns US-style string e.g. "Jan 27, 2026, 9:53 PM" or "—" for invalid input
 */
export function formatUtcTimestampForTableDisplay(
  isoString: string | null | undefined,
  timezone?: string
): string {
  if (isoString == null || isoString.trim() === '') return '—'
  try {
    const date = parseISO(isoString)
    if (!date || isNaN(date.getTime())) return '—'
    return formatDateInTimezone(date, timezone ?? getUserTimezone(), { year: true, time: true })
  } catch {
    return '—'
  }
}

/** Shared Intl core for US-style timestamps ("MMM d[, yyyy][, h:mm AM]") — callers pick the parts. */
export function formatDateInTimezone(
  date: Date,
  timezone: string,
  parts: { year?: boolean; time?: boolean } = {}
): string {
  return new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    month: 'short',
    day: 'numeric',
    ...(parts.year ? { year: 'numeric' as const } : {}),
    ...(parts.time ? { hour: 'numeric' as const, minute: '2-digit' as const, hour12: true } : {})
  }).format(date)
}

/**
 * Full table-style timestamp for HTML `title` tooltips, or `undefined` when not displayable.
 */
export function formatUtcTimestampForTooltip(isoString: string | null | undefined, timezone?: string): string | undefined {
  const full = formatUtcTimestampForTableDisplay(isoString, timezone)
  return full === '—' ? undefined : full
}

/**
 * Format a duration in seconds for compact display (e.g. impersonation session length).
 */
export function formatDurationSeconds(seconds: number | null | undefined): string {
  if (seconds == null) return '—'
  if (seconds < 60) return `${seconds}s`
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes}m ${seconds % 60}s`
  const hours = Math.floor(minutes / 60)
  return `${hours}h ${minutes % 60}m`
}

